/** Cliente HTTP da API. Centraliza CSRF, erros e formatação. */

export type ErroApi = {
  mensagem: string;
  motivos: string[];
  campos: Record<string, string>;
  status: number;
};

function lerCookie(nome: string): string | null {
  const achado = document.cookie
    .split("; ")
    .find((c) => c.startsWith(nome + "="));
  return achado ? decodeURIComponent(achado.split("=").slice(1).join("=")) : null;
}

async function requisicao<T>(url: string, init: RequestInit = {}): Promise<T> {
  const metodo = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);

  if (!(init.body instanceof FormData) && init.body) {
    headers.set("Content-Type", "application/json");
  }
  if (metodo !== "GET" && metodo !== "HEAD") {
    const csrf = lerCookie("XSRF-TOKEN");
    if (csrf) headers.set("X-XSRF-TOKEN", csrf);
  }

  const resposta = await fetch(url, { ...init, headers, credentials: "same-origin" });

  if (resposta.status === 204) return undefined as T;

  const tipo = resposta.headers.get("content-type") ?? "";
  const corpo = tipo.includes("json") ? await resposta.json().catch(() => null) : null;

  if (!resposta.ok) {
    const erro: ErroApi = {
      mensagem: corpo?.mensagem ?? corpo?.message ?? textoPadrao(resposta.status),
      motivos: corpo?.motivos ?? (corpo?.mensagem ? [corpo.mensagem] : []),
      campos: corpo?.campos ?? {},
      status: resposta.status,
    };
    throw erro;
  }
  return corpo as T;
}

function textoPadrao(status: number): string {
  if (status === 401) return "Sessão encerrada. Entre novamente.";
  if (status === 403) return "Você não tem permissão para esta ação.";
  if (status === 404) return "Não encontramos o que você procura.";
  if (status === 429) return "Muitas tentativas. Aguarde alguns minutos.";
  return "Não foi possível completar a operação. Tente novamente.";
}

export const api = {
  get: <T>(url: string) => requisicao<T>(url),
  post: <T>(url: string, corpo?: unknown) =>
    requisicao<T>(url, { method: "POST", body: corpo ? JSON.stringify(corpo) : undefined }),
  postForm: <T>(url: string, dados: FormData) =>
    requisicao<T>(url, { method: "POST", body: dados }),
  delete: <T>(url: string) => requisicao<T>(url, { method: "DELETE" }),
};

/* ----------------------------------------------------- tipos da API pública */

export type Fase = {
  codigo: string; rotulo: string; inicio: string; fim: string | null;
  abertaIndefinida: boolean; aberta: boolean; encerrada: boolean;
  ordem: number; observacao: string | null; periodoFormatado: string;
};

export type Componente = {
  id: number; codigo: string; nome: string; preRequisito: string; observacao: string | null;
};

export type Cargo = {
  id: number; codigo: string; nome: string; grupo: string; grupoNome: string;
  campoAtuacao: string; exigeComponente: boolean; cargaHoraria: string;
  remuneracao: number; qualificacaoExigida: string | null;
  limiteInscricoes: number; itemEdital: string | null; componentes: Componente[];
};

export type Cota = {
  tipo: string; rotulo: string; percentual: number; posicoes: number[];
  passoContinuacao: number; itemEdital: string | null;
};

export type Ressalva = {
  item: string; descricao: string; tratamento: string; severidade: string;
};

export type Edital = {
  id: number; numero: string; ano: number; anoLetivo: number; rotulo: string;
  titulo: string; ementa: string; orgao: string; municipio: string; uf: string;
  enderecoSemed: string; telefoneSemed: string; emailComissao: string;
  siteOficial: string; arquivoPdfPath: string; presidenteComissao: string;
  portariaComissao: string | null; maxInscricoesTotal: number; idadeMinima: number;
  consentimentoTexto: string; consentimentoVersao: string;
  cronograma: Fase[]; cargos: Cargo[]; cotas: Cota[]; ressalvas: Ressalva[];
  faseAtual: Fase | null; proximaFase: Fase | null; agora: string;
};

export type RegraExperiencia = {
  codigo: string; descricao: string; pontosPorMes: number; tetoMeses: number;
};

export type RegraQualificacao = {
  categoria: string; codigo: string; descricao: string; pontos: number;
  cargaHorariaMin: number | null; cargaHorariaMax: number | null;
  anoConclusaoMin: number | null; anoConclusaoMax: number | null;
};

export type Regras = {
  grupo: string; experiencia: RegraExperiencia[]; qualificacao: RegraQualificacao[];
  tetoMeses: number; diasPorMes: number; maximoPossivel: number;
};

/* --------------------------------------------------------- conta do candidato */

export type SessaoCandidato = { autenticado: boolean; nome: string | null; cpf: string | null };

export type CandidatoCriado = { cpf: string; temSenha: boolean };

export type CandidatoExperienciaDto = {
  id?: number; instituicao: string; vinculo: string; dataInicio: string; dataFim: string | null;
};

export type CandidatoQualificacaoDto = {
  id?: number; categoria: "I" | "II"; descricao: string; instituicao: string;
  cargaHoraria: number | null; dataConclusao: string | null;
};

export type CandidatoPerfil = {
  nome: string; cpf: string; dataNascimento: string; rg: string; rgOrgaoExpedidor: string | null;
  email: string; telefone: string; telefoneAlternativo: string | null;
  logradouro: string; numero: string; complemento: string | null; bairro: string; cidade: string;
  uf: string; cep: string; edicaoBloqueada: boolean;
  experiencias: CandidatoExperienciaDto[]; qualificacoes: CandidatoQualificacaoDto[];
};

export type InscricaoResumo = {
  id: number; cargo: string; componente: string | null; modalidade: string; status: string;
  pontosTotal: number; enviadaEm: string | null;
};

export async function entrarComoCandidato(cpf: string, senha: string): Promise<SessaoCandidato> {
  return api.post<SessaoCandidato>("/api/candidato/login", { cpf, senha });
}

export type Movimentacao = {
  titulo: string; detalhe: string | null; situacao: string; quando: string;
};

export type Posicao = {
  lista: string; listaRotulo: string; posicao: number; total: number;
  origemCota: string | null; revertida: boolean;
};

export type ConvocacaoResumo = {
  numero: string; etapa: number; titulo: string; dataEvento: string;
  localEvento: string; posicaoGeral: number; resultado: string | null;
};

export type Consulta = {
  token: string; tokenFormatado: string; nome: string; cargo: string;
  componente: string | null; situacao: string; situacaoDescricao: string;
  motivoIndeferimento: string | null;
  fotoEnviada: boolean;
  enviadaEm: string;
  historico: Movimentacao[]; posicoes: Posicao[]; convocacoes: ConvocacaoResumo[];
  recursoDisponivel: boolean; editalRotulo: string;
};

export type LinhaClassificacao = {
  posicao: number; nome: string; pontosTotal: number; pontosQualificacao: number;
  pontosExperiencia: number; dataNascimento: string;
  origemCota: string | null; revertida: boolean;
};

export type ListaPublicada = {
  lista: string; listaRotulo: string; cargo: string; componente: string | null;
  publicacao: string; publicadaEm: string; total: number; linhas: LinhaClassificacao[];
};

/* --------------------------------------------------------------- formatação */

export const fmt = {
  pontos(v: number | string | null | undefined): string {
    if (v === null || v === undefined) return "0,00";
    return Number(v).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  },
  moeda(v: number): string {
    return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  },
  data(iso: string | null | undefined): string {
    if (!iso) return "-";
    return new Date(iso).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
  },
  dataHora(iso: string | null | undefined): string {
    if (!iso) return "-";
    return new Date(iso).toLocaleString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  },
  cpf(valor: string): string {
    const d = valor.replace(/\D/g, "").slice(0, 11);
    return d
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  },
  cep(valor: string): string {
    const d = valor.replace(/\D/g, "").slice(0, 8);
    return d.replace(/(\d{5})(\d)/, "$1-$2");
  },
  telefone(valor: string): string {
    const d = valor.replace(/\D/g, "").slice(0, 11);
    if (d.length <= 10) {
      return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
    }
    return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
  },
  /**
   * Texto vindo do servidor sem remissões a item, anexo ou edital: tira os
   * trechos entre parênteses e as frases que ainda citam essas referências.
   * Devolve "" quando nada sobra, para quem chama simplesmente não exibir.
   */
  semRefs(texto: string | null | undefined): string {
    if (!texto) return "";
    const REF = /\b(item|itens|anexos?|subitem|subitens)\b|\bedital\b/i;
    const semParenteses = texto
      .replace(/\s*\([^()]*\)/g, (m) => (REF.test(m) ? "" : m))
      .replace(/,?\s*conforme (o |a )?(anexo|item)[^.,;]*/gi, "");
    return semParenteses
      .split(/(?<=[.!?])\s+/)
      .filter((frase) => !REF.test(frase))
      .join(" ")
      .trim();
  },
  token(valor: string): string {
    const d = valor.replace(/\D/g, "").slice(0, 10);
    return d.replace(/(\d{3})(\d)/, "$1 $2").replace(/(\d{3}) (\d{3})(\d)/, "$1 $2 $3");
  },
};

/** Validação de CPF no cliente, espelhando a do servidor. */
export function cpfValido(entrada: string): boolean {
  const cpf = entrada.replace(/\D/g, "");
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  const digito = (ate: number) => {
    let soma = 0;
    let peso = ate + 1;
    for (let i = 0; i < ate; i++) soma += Number(cpf[i]) * peso--;
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };
  return digito(9) === Number(cpf[9]) && digito(10) === Number(cpf[10]);
}
